'use client';

import NextImage from 'next/image';
import { useMemo } from 'react';

import { type AppImageProps } from '@/app/components/media/app-image/app-image/types';
import { getImageFetchPriority } from '@/app/components/media/app-image/shared/image-load-state';
import { TRANSPARENT_IMAGE_PLACEHOLDER_SRC } from '@/app/components/media/app-image/shared/image-placeholder';
import {
  isSvgSrc,
  normalizeSrc,
} from '@/app/components/media/app-image/shared/image-source';
import {
  buildProjectImageUrl,
  getImageAttrs,
  getProjectImageKitUrlOptions,
  nextProjectImageLoader,
  sanitizeImageAlt,
} from '@/app/components/media/app-image/shared/project-adapter';
import { preconnectImageOrigin } from '@/app/components/media/app-image/shared/resource-hints';
import { useImageLoadState } from '@/app/components/media/app-image/shared/use-image-load-state';
import {
  ImageStyles,
  ImageWrapperStyles,
} from '@/app/components/media/app-image/styles';
import { cn } from '@/app/utils/cn';

const EMPTY_IMAGEKIT_PARAMS: NonNullable<AppImageProps['params']> = {};

const resolveAppImageSource = ({
  src,
  params,
}: Pick<AppImageProps, 'src' | 'params'>) => {
  const normalizedSrc = normalizeSrc(src);
  const isSvg = isSvgSrc(normalizedSrc);
  const usesProjectImageLoader = typeof src === 'string' && !isSvg;
  const transformedSrc = usesProjectImageLoader
    ? buildProjectImageUrl(normalizedSrc, getProjectImageKitUrlOptions(params))
    : src;

  return {
    imageKey: normalizeSrc(transformedSrc),
    isSvg,
    transformedSrc,
    usesProjectImageLoader,
  };
};

const getWrapperStyleWithRatio = ({
  customRatio,
  height,
  style,
  width,
}: {
  customRatio: boolean;
  height: AppImageProps['height'];
  style: AppImageProps['style'];
  width: AppImageProps['width'];
}) => {
  const hasNumericRatio =
    customRatio === true &&
    typeof width === 'number' &&
    typeof height === 'number' &&
    width > 0 &&
    height > 0;

  if (!hasNumericRatio) return style;

  return {
    ...style,
    aspectRatio: `${width} / ${height}`,
  };
};

export const AppImage = ({
  src,
  alt,
  params = EMPTY_IMAGEKIT_PARAMS,
  fade = true,
  hoverEffect,
  objectFit = 'cover',
  objectPosition = 'center',
  placeholder = false,
  preload,
  root,
  loading,
  preloadOffset = '0px 0px 0px 0px',
  className,
  imgClassName,
  style,
  imgStyle,
  imgRef,
  customRatio = false,
  testId,
  fetchPriority,
  onLoad,
  ...props
}: AppImageProps) => {
  const resolvedSource = useMemo(
    () => resolveAppImageSource({ src, params }),
    [params, src]
  );

  if (preload) {
    preconnectImageOrigin(resolvedSource.transformedSrc);
  }

  const {
    handleLoad,
    isLoaded,
    loadingAttr,
    shouldAnimatePlaceholder,
    shouldFade,
    shouldLoadImage,
    shouldPreloadImage,
    shouldShowPlaceholder,
    wrapperRef,
  } = useImageLoadState({
    imageKey: resolvedSource.imageKey,
    fade: !!fade,
    placeholder: !!placeholder,
    preload,
    loading,
    root,
    preloadOffset,
    onLoad,
  });

  const renderedImageSrc = shouldLoadImage
    ? resolvedSource.transformedSrc
    : TRANSPARENT_IMAGE_PLACEHOLDER_SRC;
  const fetchPriorityAttr = getImageFetchPriority({ fetchPriority });
  const wrapperStyleWithRatio = getWrapperStyleWithRatio({
    customRatio,
    height: props.height,
    style,
    width: props.width,
  });
  const wrapperClassName = cn(
    ImageWrapperStyles({
      fill: props.fill,
      placeholder: shouldShowPlaceholder,
      animatePlaceholder: shouldAnimatePlaceholder,
      isLoaded: shouldShowPlaceholder && isLoaded,
    }),
    className
  );
  const imageClassName = cn(
    ImageStyles({
      fade: shouldFade,
      hoverEffect,
      objectFit,
      objectPosition,
    }),
    {
      'opacity-100': isLoaded && shouldFade,
      'is-loaded': isLoaded,
    },
    imgClassName
  );

  if (!src) return null;

  return (
    <figure
      ref={wrapperRef}
      className={wrapperClassName}
      {...getImageAttrs({ testid: testId ?? 'app-image' })}
      style={wrapperStyleWithRatio}
    >
      <NextImage
        key={resolvedSource.imageKey}
        {...props}
        ref={imgRef}
        className={imageClassName}
        style={imgStyle}
        fetchPriority={fetchPriorityAttr}
        loading={loadingAttr}
        preload={shouldPreloadImage || undefined}
        src={renderedImageSrc}
        alt={sanitizeImageAlt(alt)}
        loader={
          resolvedSource.usesProjectImageLoader
            ? nextProjectImageLoader
            : undefined
        }
        unoptimized={
          resolvedSource.isSvg ||
          renderedImageSrc === TRANSPARENT_IMAGE_PLACEHOLDER_SRC
        }
        onLoad={handleLoad}
        {...getImageAttrs({ testid: 'image' })}
      />
    </figure>
  );
};
